import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, forkJoin, of } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ControltaskService, ControlTask } from '../../services/controltask.service';
import { UserSessionService } from '../../services/user-session.service';
import { API_BASE_URL } from '../../services/api-base';
import { toLocalISOString } from '../../utils/date-utils';
import { TranslateService } from '@ngx-translate/core';

interface ControlTaskSummary {
  total_tasks?: number;
  open_tasks?: number;
  in_progress_tasks?: number;
  blocked_tasks?: number;
  completed_tasks?: number;
}

interface DraftBucket {
  name: string;
  description?: string;
  color?: string;
  order_index?: number;
}

interface DraftSubgroup {
  name: string;
  description?: string;
  color?: string;
  buckets: DraftBucket[];
  bucketDraft: DraftBucket;
}

@Component({
  selector: 'app-controltask',
  templateUrl: './controltask.component.html',
  styleUrls: ['./controltask.component.scss']
})
export class ControltaskComponent implements OnInit {
  tasks: ControlTask[] = [];
  summary: ControlTaskSummary = {};
  groups: any[] = [];
  subgroups: any[] = [];
  buckets: any[] = [];
  tags: any[] = [];
  members: any[] = [];
  loading = false;
  groupsLoaded = false;
  serverDateFilter = false;
  tenantId: number | null = null;
  userId: number | null = null;
  userDepartmentId: number | null = null;

  showTaskModal = false;
  showGroupModal = false;
  showTagModal = false;
  showSubgroupModal = false;
  showBucketModal = false;
  showGroupActions = false;
  showTaskActions = false;
  showFilterPanel = false;
  showTaskDetailModal = false;
  inlineBucketLoading = false;

  taskForm: FormGroup;
  taskDetailForm: FormGroup;
  groupForm: FormGroup;
  tagForm: FormGroup;
  subgroupForm: FormGroup;
  bucketForm: FormGroup;

  draftSubtasks: string[] = [];
  newTaskFiles: File[] = [];
  statusFilter = 'ALL';
  groupSubgroupsDraft: DraftSubgroup[] = [];
  groupSubgroupDraft = { name: '', description: '', color: '' };
  subgroupBucketsDraft: DraftBucket[] = [];
  subgroupBucketDraft: DraftBucket = { name: '', description: '', color: '', order_index: 0 };
  openGroupIds = new Set<number>();
  listFilter: 'all' | 'today' | 'important' | 'group' = 'all';
  selectedGroupId: number | null = null;
  selectedSubgroupId: number | null = null;
  filterGroupId: number | '' = '';
  filterBucketId: number | '' = '';
  searchTerm = '';
  currentPage = 1;
  pageSize = 5;
  completedPage = 1;
  completedExpanded = false;
  activeTask: ControlTask | null = null;
  detailSubtaskText = '';
  detailCommentText = '';
  detailCommentFile: File | null = null;
  editingSubtaskId: number | null = null;
  editingSubtaskText = '';
  editingCommentId: number | null = null;
  editingCommentText = '';
  private commentFileInputElement?: HTMLInputElement | null;
  private readonly fileHostUrl = `${API_BASE_URL}`;

  constructor(
    private fb: FormBuilder,
    private service: ControltaskService,
    private session: UserSessionService,
    private translate: TranslateService
  ) {
    this.taskForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      priority: ['MEDIUM'],
      visibility: ['PRIVATE'],
      status: ['OPEN'],
      start_date: [''],
      due_date: [''],
      subgroupId: [''],
      bucketId: [''],
      groupId: [''],
      tagIds: [[]],
      assigneeId: [''],
      newSubtask: [''],
      inlineBucketName: [''],
      inlineBucketDescription: ['']
    });

    this.groupForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this.tagForm = this.fb.group({
      name: ['', Validators.required],
      color: ['']
    });

    this.subgroupForm = this.fb.group({
      group_id: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      color: ['']
    });

    this.bucketForm = this.fb.group({
      group_id: ['', Validators.required],
      subgroup_id: ['', Validators.required],
      name: ['', Validators.required],
      description: [''],
      color: [''],
      order_index: [0]
    });

    this.taskDetailForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      status: ['OPEN'],
      priority: ['MEDIUM'],
      visibility: ['PRIVATE'],
      start_date: [''],
      due_date: [''],
      groupId: [''],
      subgroupId: [''],
      bucketId: [''],
      tagIds: [[]],
      assigneeId: ['']
    });
  }

  ngOnInit(): void {
    const currentUser = this.session.getUser();
    this.tenantId = currentUser?.tenant_id || null;
    this.userId = currentUser?.id || null;
    this.userDepartmentId = (currentUser as any)?.['department_id'] ?? (currentUser as any)?.['department']?.['id'] ?? null;
    if (this.tenantId) {
      this.loadSummary();
      this.loadTasks();
      this.loadGroups();
      this.loadTags();
      this.loadMembers();
      this.loadSubgroups();
      this.loadBuckets();
    }
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.action-icons')) {
      this.showGroupActions = false;
      this.showTaskActions = false;
    }
  }

  loadSummary() {
    if (!this.tenantId) {
      return;
    }
    this.service.getDashboard(this.tenantId, {
      user_id: this.userId,
      department_id: this.userDepartmentId
    }).subscribe({
      next: (summary: any) => (this.summary = summary || {}),
      error: () => (this.summary = {})
    });
  }

  loadGroups() {
    if (!this.tenantId) {
      return;
    }
    this.service.getGroups(this.tenantId, {
      user_id: this.userId,
      department_id: this.userDepartmentId
    }).subscribe({
      next: (groups) => {
        this.groups = groups;
        this.groupsLoaded = true;
      },
      error: () => {
        this.groups = [];
        this.groupsLoaded = true;
      }
    });
  }

  loadTags() {
    if (!this.tenantId) {
      return;
    }
    this.service.getTags(this.tenantId).subscribe({
      next: (tags) => (this.tags = tags),
      error: () => (this.tags = [])
    });
  }

  loadSubgroups() {
    if (!this.tenantId) {
      return;
    }
    this.service.getSubgroups(this.tenantId, undefined, {
      user_id: this.userId,
      department_id: this.userDepartmentId
    }).subscribe({
      next: (rows) => (this.subgroups = rows),
      error: () => (this.subgroups = [])
    });
  }

  loadBuckets() {
    if (!this.tenantId) {
      return;
    }
    this.service.getBuckets(this.tenantId).subscribe({
      next: (rows) => (this.buckets = rows),
      error: () => (this.buckets = [])
    });
  }

  loadMembers() {
    if (!this.tenantId) {
      return;
    }
    this.service.getUsers(this.tenantId).subscribe({
      next: (users) => (this.members = users),
      error: () => (this.members = [])
    });
  }

  loadTasks() {
    if (!this.tenantId) {
      return;
    }
    this.loading = true;
    this.serverDateFilter = false;
    const filter: Record<string, any> = {};
    if (this.statusFilter !== 'ALL') {
      filter['status'] = this.statusFilter;
    }
    if (this.listFilter === 'today') {
      const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
      if (todayKey) {
        filter['start_date'] = todayKey;
        this.serverDateFilter = true;
      }
    }
    let appliedGroupId: number | null = null;
    if (this.listFilter !== 'today' && this.listFilter === 'group' && this.selectedGroupId) {
      appliedGroupId = this.selectedGroupId;
      if (this.selectedSubgroupId) {
        filter['subgroup_id'] = this.selectedSubgroupId;
      }
    } else if (this.listFilter !== 'today' && this.selectedSubgroupId) {
      filter['subgroup_id'] = this.selectedSubgroupId;
    }
    if (!appliedGroupId && this.listFilter !== 'today' && this.filterGroupId) {
      appliedGroupId = Number(this.filterGroupId);
    }
    if (appliedGroupId && this.listFilter !== 'today') {
      filter['group_id'] = appliedGroupId;
    }
    if (this.listFilter !== 'today' && this.filterBucketId) {
      filter['bucket_id'] = Number(this.filterBucketId);
    }
    if (this.userId) {
      filter['user_id'] = this.userId;
    }
    if (this.userDepartmentId) {
      filter['department_id'] = this.userDepartmentId;
    }
    this.service.getTasks(this.tenantId, filter).subscribe({
      next: (tasks) => {
        this.tasks = tasks || [];
        this.loading = false;
        this.currentPage = 1;
      },
      error: () => {
        this.tasks = [];
        this.loading = false;
      }
    });
  }

  get columns() {
    return [
      { id: 'OPEN', title: this.translate.instant('CONTROLTASK.STATUS.OPEN') },
      { id: 'IN_PROGRESS', title: this.translate.instant('CONTROLTASK.STATUS.IN_PROGRESS') },
      { id: 'BLOCKED', title: this.translate.instant('CONTROLTASK.STATUS.BLOCKED') },
      { id: 'COMPLETED', title: this.translate.instant('CONTROLTASK.STATUS.COMPLETED') }
    ];
  }

  openTaskModal() {
    this.taskForm.reset({
      title: '',
      description: '',
      priority: 'MEDIUM',
      visibility: 'PRIVATE',
      status: 'OPEN',
      start_date: '',
      due_date: '',
      subgroupId: this.selectedSubgroupId || '',
      bucketId: '',
      groupId: this.selectedGroupId || '',
      tagIds: [],
      assigneeId: '',
      inlineBucketName: '',
      inlineBucketDescription: ''
    });
    this.handleGroupSelectionChange();
    this.handleSubgroupSelectionChange();
    this.draftSubtasks = [];
    this.newTaskFiles = [];
    this.showTaskModal = true;
  }

  openTaskFromMenu() {
    this.openTaskModal();
    this.showTaskActions = false;
  }

  closeTaskModal() {
    this.showTaskModal = false;
    this.newTaskFiles = [];
  }

  addDraftSubtask() {
    const text = (this.taskForm.get('newSubtask')?.value || '').trim();
    if (!text) {
      return;
    }
    this.draftSubtasks.push(text);
    this.taskForm.get('newSubtask')?.reset('');
  }

  removeDraftSubtask(index: number) {
    this.draftSubtasks.splice(index, 1);
  }

  onTaskFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files || []);
    if (files.length) {
      this.newTaskFiles = [...this.newTaskFiles, ...files];
    }
    if (input) {
      input.value = '';
    }
  }

  removeTaskFile(index: number) {
    this.newTaskFiles = this.newTaskFiles.filter((_item, i) => i !== index);
  }

  submitTask() {
    if (!this.taskForm.valid || !this.tenantId || !this.userId) {
      return;
    }
    const value = this.taskForm.value;
    const payload = {
      tenant_id: this.tenantId,
      title: value.title,
      description: value.description,
      priority: value.priority,
      visibility: value.visibility,
      status: value.status,
      due_date: value.due_date || null,
      start_date: value.start_date || null,
      created_by: this.userId,
      subgroup_id: value.subgroupId || null,
      bucket_id: value.bucketId || null,
      groups: value.groupId ? [Number(value.groupId)] : [],
      tags: value.tagIds || [],
      assignees: value.assigneeId ? [Number(value.assigneeId)] : [],
      subtasks: this.draftSubtasks,
      task_code: this.generateTaskCode()
    };
    this.service.createTask(payload).subscribe({
      next: (response) => {
        const taskId = response?.id;
        if (taskId && this.newTaskFiles.length) {
          this.uploadTaskAttachments(taskId);
          return;
        }
        this.showTaskModal = false;
        this.newTaskFiles = [];
        this.loadTasks();
        this.loadSummary();
      }
    });
  }

  private uploadTaskAttachments(taskId: number) {
    if (!this.userId || !this.newTaskFiles.length) {
      this.showTaskModal = false;
      this.newTaskFiles = [];
      this.loadTasks();
      this.loadSummary();
      return;
    }
    const uploads = this.newTaskFiles.map((file) => this.service.uploadAttachment(taskId, file, this.userId as number));
    forkJoin(uploads.length ? uploads : [of(null)]).subscribe({
      next: () => {
        this.showTaskModal = false;
        this.newTaskFiles = [];
        this.loadTasks();
        this.loadSummary();
      },
      error: () => {
        this.showTaskModal = false;
        this.newTaskFiles = [];
        this.loadTasks();
        this.loadSummary();
      }
    });
  }

  toggleGroupMenu(event: Event) {
    event.stopPropagation();
    this.showGroupActions = !this.showGroupActions;
    if (this.showGroupActions) {
      this.showTaskActions = false;
    }
  }

  toggleTaskMenu(event: Event) {
    event.stopPropagation();
    this.showTaskActions = !this.showTaskActions;
    if (this.showTaskActions) {
      this.showGroupActions = false;
    }
  }

  openGroupModal() {
    this.groupForm.reset();
    this.resetGroupDrafts();
    this.showGroupModal = true;
    this.showGroupActions = false;
  }

  openStandaloneSubgroupModal() {
    this.subgroupForm.reset();
    this.subgroupBucketsDraft = [];
    this.subgroupBucketDraft = this.defaultBucketDraft();
    this.showSubgroupModal = true;
    this.showGroupActions = false;
  }

  openStandaloneBucketModal() {
    this.bucketForm.reset({ group_id: '', subgroup_id: '', name: '', description: '', color: '', order_index: 0 });
    this.showBucketModal = true;
    this.showGroupActions = false;
  }

  private resetGroupDrafts() {
    this.groupSubgroupsDraft = [];
    this.groupSubgroupDraft = { name: '', description: '', color: '' };
  }

  private defaultBucketDraft(order = 0): DraftBucket {
    return { name: '', description: '', color: '', order_index: order };
  }

  addDraftSubgroup() {
    if (!this.groupSubgroupDraft.name.trim()) {
      return;
    }
    this.groupSubgroupsDraft.push({
      name: this.groupSubgroupDraft.name.trim(),
      description: this.groupSubgroupDraft.description || '',
      color: this.groupSubgroupDraft.color || '',
      buckets: [],
      bucketDraft: this.defaultBucketDraft()
    });
    this.groupSubgroupDraft = { name: '', description: '', color: '' };
  }

  removeDraftSubgroup(index: number) {
    this.groupSubgroupsDraft.splice(index, 1);
  }

  addDraftBucketToGroupSub(index: number) {
    const subgroup = this.groupSubgroupsDraft[index];
    if (!subgroup.bucketDraft.name?.trim()) {
      return;
    }
    const order = subgroup.bucketDraft.order_index ?? subgroup.buckets.length;
    subgroup.buckets.push({
      name: subgroup.bucketDraft.name.trim(),
      description: subgroup.bucketDraft.description || '',
      color: subgroup.bucketDraft.color || '',
      order_index: order
    });
    subgroup.bucketDraft = this.defaultBucketDraft(subgroup.buckets.length);
  }

  removeDraftBucket(subIndex: number, bucketIndex: number) {
    this.groupSubgroupsDraft[subIndex].buckets.splice(bucketIndex, 1);
  }

  addSubgroupBucketDraft() {
    if (!this.subgroupBucketDraft.name?.trim()) {
      return;
    }
    const order = this.subgroupBucketDraft.order_index ?? this.subgroupBucketsDraft.length;
    this.subgroupBucketsDraft.push({
      name: this.subgroupBucketDraft.name.trim(),
      description: this.subgroupBucketDraft.description || '',
      color: this.subgroupBucketDraft.color || '',
      order_index: order
    });
    this.subgroupBucketDraft = this.defaultBucketDraft(this.subgroupBucketsDraft.length);
  }

  removeSubgroupBucketDraft(index: number) {
    this.subgroupBucketsDraft.splice(index, 1);
  }

  async submitGroup() {
    if (!this.groupForm.valid || !this.tenantId || !this.userId) {
      return;
    }
    const value = this.groupForm.value;
    const payload = {
      tenant_id: this.tenantId,
      name: value.name,
      description: value.description,
      created_by: this.userId
    };
    this.service.createGroup(payload).subscribe({
      next: async (group: any) => {
        if (group?.id && this.groupSubgroupsDraft.length && this.tenantId) {
          for (const draft of this.groupSubgroupsDraft) {
            const createdSubgroup: any = await firstValueFrom(
              this.service.createSubgroup({
                tenant_id: this.tenantId,
                group_id: group.id,
                name: draft.name,
                description: draft.description || null,
                color: draft.color || null,
                created_by: this.userId
              })
            );
            if (createdSubgroup?.id && draft.buckets.length) {
              for (const [index, bucket] of draft.buckets.entries()) {
                await firstValueFrom(
                  this.service.createBucket({
                    tenant_id: this.tenantId,
                    subgroup_id: createdSubgroup.id,
                    name: bucket.name,
                    description: bucket.description || null,
                    color: bucket.color || null,
                    order_index: bucket.order_index ?? index
                  })
                );
              }
            }
          }
        }
        this.groupForm.reset();
        this.resetGroupDrafts();
        this.showGroupModal = false;
        this.loadGroups();
        this.loadSubgroups();
        this.loadBuckets();
      }
    });
  }

  submitSubgroup() {
    if (!this.subgroupForm.valid || !this.tenantId || !this.userId) {
      return;
    }
    const value = this.subgroupForm.value;
    const payload = {
      tenant_id: this.tenantId,
      group_id: value.group_id,
      name: value.name,
      description: value.description,
      color: value.color,
      created_by: this.userId
    };
    this.service.createSubgroup(payload).subscribe({
      next: async (subgroup: any) => {
        if (subgroup?.id && this.subgroupBucketsDraft.length && this.tenantId) {
          for (const [index, bucket] of this.subgroupBucketsDraft.entries()) {
            await firstValueFrom(
              this.service.createBucket({
                tenant_id: this.tenantId,
                subgroup_id: subgroup.id,
                name: bucket.name,
                description: bucket.description || null,
                color: bucket.color || null,
                order_index: bucket.order_index ?? index
              })
            );
          }
        }
        this.subgroupForm.reset();
        this.subgroupBucketsDraft = [];
        this.subgroupBucketDraft = this.defaultBucketDraft();
        this.showSubgroupModal = false;
        this.loadSubgroups();
        this.loadBuckets();
      }
    });
  }

  submitBucket() {
    if (!this.bucketForm.valid || !this.tenantId) {
      return;
    }
    const value = this.bucketForm.value;
    const payload = {
      tenant_id: this.tenantId,
      subgroup_id: value.subgroup_id,
      name: value.name,
      description: value.description,
      color: value.color,
      order_index: value.order_index || 0
    };
    this.service.createBucket(payload).subscribe({
      next: () => {
        this.bucketForm.reset({ group_id: '', subgroup_id: '', name: '', description: '', color: '', order_index: 0 });
        this.showBucketModal = false;
        this.loadBuckets();
      }
    });
  }

  submitTag() {
    if (!this.tagForm.valid || !this.tenantId) {
      return;
    }
    const value = this.tagForm.value;
    const payload = {
      tenant_id: this.tenantId,
      name: value.name,
      color: value.color
    };
    this.service.createTag(payload).subscribe({
      next: () => {
        this.tagForm.reset();
        this.showTagModal = false;
        this.loadTags();
      }
    });
  }

  changeTaskStatus(task: ControlTask, status: string) {
    if (task.status === status) {
      return;
    }
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      status
    };
    this.service.updateTask(task.id, payload).subscribe({
      next: (updated: any) => {
        if (updated && typeof updated === 'object') {
          Object.assign(task, updated);
        } else {
          task.status = status;
          if (status === 'IN_PROGRESS' && !task.execution_started_at) {
            task.execution_started_at = new Date().toISOString();
          }
          if (status !== 'IN_PROGRESS') {
            task.execution_started_at = null;
          }
        }
        if (this.activeTask?.id === task.id) {
          this.activeTask = { ...task };
        }
        this.loadSummary();
        this.loadTasks();
      }
    });
  }

  toggleTaskExecution(task: ControlTask, checked: boolean) {
    this.changeTaskStatus(task, checked ? 'IN_PROGRESS' : 'PAUSED');
  }

  canToggleTaskExecution(task: ControlTask) {
    return task.status !== 'COMPLETED' && task.status !== 'CANCELLED';
  }

  onStatusSelect(task: ControlTask, event: Event) {
    const select = event.target as HTMLSelectElement | null;
    if (!select) {
      return;
    }
    this.changeTaskStatus(task, select.value);
  }

  toggleSubtask(task: ControlTask, subtask: any) {
    const completed = !subtask.completed;
    const payload: any = { completed };
    if (!completed) {
      payload.completed_at = null;
    }
    this.service.updateSubtask(subtask.id, payload).subscribe({
      next: () => {
        subtask.completed = completed;
        subtask.completed_at = completed ? toLocalISOString(new Date()) : null;
      }
    });
  }

  toggleFilterPanel() {
    this.showFilterPanel = !this.showFilterPanel;
  }

  closeFilterPanel() {
    this.showFilterPanel = false;
  }

  addSubtask(task: ControlTask, event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    const value = (input.value || '').trim();
    if (!value) {
      return;
    }
    this.service.createSubtask(task.id, { title: value }).subscribe({
      next: (response: any) => {
        task.subtasks = task.subtasks || [];
        task.subtasks.push({
          id: response.id,
          title: value,
          completed: 0
        });
        input.value = '';
      }
    });
  }

  uploadAttachment(task: ControlTask, event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.userId) {
      return;
    }
    const file = input.files[0];
    this.service.uploadAttachment(task.id, file, this.userId).subscribe({
      next: (attachment: any) => {
        task.attachments = task.attachments || [];
        task.attachments.push(attachment);
        input.value = '';
      },
      error: () => {
        input.value = '';
      }
    });
  }

  removeAttachment(task: ControlTask, attachment: any) {
    this.service.removeAttachment(attachment.id).subscribe({
      next: () => {
        task.attachments = (task.attachments || []).filter((item) => item.id !== attachment.id);
      }
    });
  }

  toggleCompletion(task: ControlTask, event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input) {
      return;
    }
    this.changeTaskStatus(task, input.checked ? 'COMPLETED' : 'OPEN');
  }

  selectQuickFilter(filter: 'all' | 'today' | 'important') {
    this.listFilter = filter;
    this.selectedGroupId = null;
    this.selectedSubgroupId = null;
    this.currentPage = 1;
    this.completedPage = 1;
    this.loadTasks();
  }

  selectTodayTasks() {
    this.listFilter = 'today';
    this.selectedGroupId = null;
    this.selectedSubgroupId = null;
    this.filterGroupId = '';
    this.filterBucketId = '';
    this.currentPage = 1;
    this.completedPage = 1;
    this.loadTasks();
  }

  selectGroup(groupId: number, subgroupId?: number) {
    this.listFilter = 'group';
    this.selectedGroupId = groupId;
    this.selectedSubgroupId = subgroupId ?? null;
    this.currentPage = 1;
    this.completedPage = 1;
    this.loadTasks();
  }

  clearGroupFilter() {
    this.listFilter = 'all';
    this.selectedGroupId = null;
    this.selectedSubgroupId = null;
    this.filterGroupId = '';
    this.filterBucketId = '';
    this.currentPage = 1;
    this.completedPage = 1;
    this.loadTasks();
  }

  clearAllFilters() {
    this.statusFilter = 'ALL';
    this.searchTerm = '';
    this.listFilter = 'all';
    this.selectedGroupId = null;
    this.selectedSubgroupId = null;
    this.filterGroupId = '';
    this.filterBucketId = '';
    this.currentPage = 1;
    this.completedPage = 1;
    this.showFilterPanel = false;
    this.loadTasks();
  }

  get filteredTasks(): ControlTask[] {
    const term = this.searchTerm.trim().toLowerCase();
    return this.tasks.filter((task) => {
      let matches = true;

      if (this.listFilter === 'today') {
        matches = this.serverDateFilter ? true : this.isStartToday(task);
      } else if (this.listFilter === 'important') {
        matches = this.isImportant(task);
      } else if (this.listFilter === 'group' && this.selectedGroupId) {
        const matchesGroup = !!task.groups?.some((group: any) => group.id === this.selectedGroupId);
        const matchesSubgroup =
          !!task.subgroup_id &&
          this.subgroups.some(
            (subgroup) => subgroup.id === task.subgroup_id && subgroup.group_id === this.selectedGroupId
          );
        matches = matchesGroup || matchesSubgroup;
        if (matches && this.selectedSubgroupId) {
          matches = task.subgroup_id === this.selectedSubgroupId;
        }
      }

      if (matches && this.selectedSubgroupId && this.listFilter !== 'group') {
        matches = task.subgroup_id === this.selectedSubgroupId;
      }

      if (matches && term) {
        const haystack = `${task.task_code || ''} ${task.title || ''} ${this.formatGroupTrail(task)} ${this.bucketName(task)}`.toLowerCase();
        matches = haystack.includes(term);
      }

      return matches;
    });
  }

  get activeFilteredTasks(): ControlTask[] {
    return this.filteredTasks.filter((task) => task.status !== 'COMPLETED');
  }

  get completedFilteredTasks(): ControlTask[] {
    return this.filteredTasks.filter((task) => task.status === 'COMPLETED');
  }

  get paginatedActiveTasks(): ControlTask[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.activeFilteredTasks.slice(start, start + this.pageSize);
  }

  get paginatedCompletedTasks(): ControlTask[] {
    const start = (this.completedPage - 1) * this.pageSize;
    return this.completedFilteredTasks.slice(start, start + this.pageSize);
  }

  get activeTotalPages(): number {
    return Math.max(1, Math.ceil(this.activeFilteredTasks.length / this.pageSize));
  }

  get completedTotalPages(): number {
    return Math.max(1, Math.ceil(this.completedFilteredTasks.length / this.pageSize));
  }

  get currentListLabel(): string {
    if (this.listFilter === 'today') {
      return this.translate.instant('CONTROLTASK.LIST.TODAY');
    }
    if (this.listFilter === 'important') {
      return this.translate.instant('CONTROLTASK.LIST.IMPORTANT');
    }
    if (this.listFilter === 'group' && this.selectedGroupId) {
      const group = this.groups.find((item) => item.id === this.selectedGroupId);
      if (this.selectedSubgroupId) {
        const subgroup = this.subgroups.find((item) => item.id === this.selectedSubgroupId);
        return subgroup ? subgroup.name : (group ? group.name : this.translate.instant('CONTROLTASK.LIST.GROUP_FALLBACK'));
      }
      return group ? group.name : this.translate.instant('CONTROLTASK.LIST.GROUP_FALLBACK');
    }
    return this.translate.instant('CONTROLTASK.LIST.MINE');
  }

  get hasActiveFilters(): boolean {
    return (
      this.statusFilter !== 'ALL' ||
      this.listFilter !== 'all' ||
      !!this.selectedGroupId ||
      !!this.selectedSubgroupId ||
      !!this.filterGroupId ||
      !!this.filterBucketId ||
      !!this.searchTerm.trim()
    );
  }

  get activeFilterLabels(): string[] {
    const labels: string[] = [];

    if (this.listFilter === 'today') {
      labels.push(this.translate.instant('CONTROLTASK.FILTER.TODAY'));
    } else if (this.listFilter === 'important') {
      labels.push(this.translate.instant('CONTROLTASK.FILTER.IMPORTANT'));
    } else if (this.listFilter === 'group' && this.selectedGroupId) {
      if (this.selectedSubgroupId) {
        const subgroup = this.subgroups.find((item) => item.id === this.selectedSubgroupId);
        labels.push(
          this.translate.instant('CONTROLTASK.FILTER.SUBGROUP', {
            name: subgroup?.name || this.translate.instant('CONTROLTASK.FILTER.SELECTED')
          })
        );
      } else {
        const group = this.groups.find((item) => item.id === this.selectedGroupId);
        labels.push(
          this.translate.instant('CONTROLTASK.FILTER.GROUP', {
            name: group?.name || this.translate.instant('CONTROLTASK.FILTER.SELECTED')
          })
        );
      }
    }

    if (this.statusFilter !== 'ALL') {
      labels.push(
        this.translate.instant('CONTROLTASK.FILTER.STATUS', {
          status: this.translateStatus(this.statusFilter)
        })
      );
    }

    if (this.filterGroupId && this.listFilter !== 'group') {
      const group = this.groups.find((item) => item.id === Number(this.filterGroupId));
      labels.push(
        this.translate.instant('CONTROLTASK.FILTER.GROUP', {
          name: group?.name || this.translate.instant('CONTROLTASK.FILTER.SELECTED')
        })
      );
    }

    if (this.selectedSubgroupId && this.listFilter !== 'group') {
      const subgroup = this.subgroups.find((item) => item.id === this.selectedSubgroupId);
      labels.push(
        this.translate.instant('CONTROLTASK.FILTER.SUBGROUP', {
          name: subgroup?.name || this.translate.instant('CONTROLTASK.FILTER.SELECTED')
        })
      );
    }

    if (this.filterBucketId) {
      const bucket = this.buckets.find((item) => item.id === Number(this.filterBucketId));
      labels.push(
        this.translate.instant('CONTROLTASK.FILTER.BUCKET', {
          name: bucket?.name || this.translate.instant('CONTROLTASK.FILTER.SELECTED')
        })
      );
    }

    const term = this.searchTerm.trim();
    if (term) {
      labels.push(this.translate.instant('CONTROLTASK.FILTER.SEARCH', { term }));
    }

    return labels;
  }

  get todayTasksCount(): number {
    return this.tasks.filter((task) => task.status !== 'COMPLETED' && this.isStartToday(task)).length;
  }

  get importantTasksCount(): number {
    return this.tasks.filter((task) => task.status !== 'COMPLETED' && this.isImportant(task)).length;
  }

  onFilterGroupChange(value: any) {
    this.filterGroupId = value ? Number(value) : '';
    this.filterBucketId = '';
    this.currentPage = 1;
    this.completedPage = 1;
    this.loadTasks();
  }

  onFilterBucketChange(value: any) {
    this.filterBucketId = value ? Number(value) : '';
    this.currentPage = 1;
    this.completedPage = 1;
    this.loadTasks();
  }

  private isStartToday(task: ControlTask): boolean {
    if (!task.start_date) {
      return false;
    }
    const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
    const startKey = this.dateKey(task.start_date, 'America/Sao_Paulo');
    return !!todayKey && !!startKey && todayKey === startKey;
  }

  private dateKey(value?: unknown, timeZone = 'America/Sao_Paulo'): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      return this.toDateKey(value, timeZone);
    }
    const normalized = String(value);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return this.toDateKey(parsed, timeZone);
  }

  private toDateKey(date: Date, timeZone: string): string {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(date);
  }

  isImportant(task: ControlTask): boolean {
    return task.priority === 'HIGH' || task.priority === 'URGENT';
  }

  tasksCountForGroup(groupId: number): number {
    return this.tasks.filter((task) => task.groups?.some((group: any) => group.id === groupId)).length;
  }

  subgroupTaskCount(subgroupId: number): number {
    return this.tasks.filter((task) => {
      if (task.subgroup_id !== subgroupId) {
        return false;
      }
      return task.status !== 'COMPLETED' && task.status !== 'BLOCKED';
    }).length;
  }

  subgroupsForGroup(groupId: number) {
    return this.subgroups.filter((subgroup) => subgroup.group_id === groupId);
  }

  subgroupCount(groupId: number): number {
    return this.subgroupsForGroup(groupId).length;
  }

  get bucketFormSubgroups() {
    const groupId = Number(this.bucketForm.get('group_id')?.value) || null;
    if (!groupId) {
      return [];
    }
    return this.subgroups.filter((subgroup) => subgroup.group_id === groupId);
  }

  onBucketGroupChange() {
    this.bucketForm.patchValue({ subgroup_id: '' });
  }

  isGroupOpen(groupId: number): boolean {
    return this.openGroupIds.has(groupId);
  }

  toggleGroup(groupId: number): void {
    if (this.openGroupIds.has(groupId)) {
      this.openGroupIds.delete(groupId);
    } else {
      this.openGroupIds.add(groupId);
    }
  }

  get availableFormSubgroups() {
    const selectedGroup = Number(this.taskForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      return this.subgroups;
    }
    return this.subgroups.filter((subgroup) => subgroup.group_id === selectedGroup);
  }

  get availableFormBuckets() {
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      return [];
    }
    return this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
  }

  get bucketFilterOptions() {
    const groupId = this.filterGroupId ? Number(this.filterGroupId) : null;
    if (!groupId) {
      return this.buckets;
    }
    const subgroupIds = this.subgroupsForGroup(groupId).map((subgroup) => subgroup.id);
    return this.buckets.filter((bucket) => subgroupIds.includes(bucket.subgroup_id));
  }

  handleGroupSelectionChange() {
    const selectedGroup = Number(this.taskForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      this.taskForm.patchValue({ subgroupId: '', bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      return;
    }
    const currentSubgroup = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (currentSubgroup) {
      const subgroup = this.subgroups.find((item) => item.id === currentSubgroup);
      if (!subgroup || subgroup.group_id !== selectedGroup) {
        this.taskForm.patchValue({ subgroupId: '', bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      }
    }
  }

  handleSubgroupSelectionChange() {
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      this.taskForm.patchValue({ bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      return;
    }
    const availableBuckets = this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
    const currentBucket = Number(this.taskForm.get('bucketId')?.value) || null;
    if (currentBucket) {
      const bucket = this.buckets.find((item) => item.id === currentBucket);
      if (!bucket || bucket.subgroup_id !== subgroupId) {
        this.taskForm.patchValue({ bucketId: '' });
      }
    }
    if (!currentBucket && availableBuckets.length) {
      this.taskForm.patchValue({ bucketId: availableBuckets[0].id, inlineBucketName: '', inlineBucketDescription: '' });
    }
  }

  createInlineBucket() {
    if (!this.tenantId) {
      return;
    }
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    const bucketName = (this.taskForm.get('inlineBucketName')?.value || '').trim();
    const description = (this.taskForm.get('inlineBucketDescription')?.value || '').trim();
    if (!subgroupId || !bucketName) {
      return;
    }
    this.inlineBucketLoading = true;
    const payload = {
      tenant_id: this.tenantId,
      subgroup_id: subgroupId,
      name: bucketName,
      description: description || null,
      color: null,
      order_index: 0
    };
    this.service.createBucket(payload).subscribe({
      next: (bucket: any) => {
        this.inlineBucketLoading = false;
        if (bucket?.id) {
          this.buckets = [...this.buckets, bucket];
          this.taskForm.patchValue({ bucketId: bucket.id });
        } else {
          this.loadBuckets();
        }
        this.taskForm.patchValue({ inlineBucketName: '', inlineBucketDescription: '' });
      },
      error: () => {
        this.inlineBucketLoading = false;
      }
    });
  }

  bucketName(task: ControlTask) {
    return task.bucket_name || this.translate.instant('CONTROLTASK.NO_BUCKET');
  }

  priorityClass(priority: string) {
    const normalized = (priority || '').toLowerCase();
    return {
      low: normalized === 'low',
      medium: normalized === 'medium',
      high: normalized === 'high',
      urgent: normalized === 'urgent'
    };
  }

  formatGroupTrail(task: ControlTask) {
    const groupNamesText = this.groupNames(task);
    if (task.subgroup_name) {
      return groupNamesText ? `${groupNamesText} / ${task.subgroup_name}` : task.subgroup_name;
    }
    return groupNamesText || this.translate.instant('CONTROLTASK.NO_GROUP');
  }

  subtaskProgress(task: ControlTask) {
    if (!task.subtasks?.length) {
      return 0;
    }
    const completed = task.subtasks.filter((item) => !!item.completed).length;
    return Math.round((completed / task.subtasks.length) * 100);
  }

  trackByTask(_index: number, item: ControlTask) {
    return item.id;
  }

  groupNames(task: ControlTask): string {
    if (!task.groups?.length) {
      return '';
    }
    return task.groups.map((group: any) => group.name).join(', ');
  }

  onSearchChange() {
    this.currentPage = 1;
    this.completedPage = 1;
  }

  goToPage(direction: 'prev' | 'next') {
    if (direction === 'prev' && this.currentPage > 1) {
      this.currentPage--;
    } else if (direction === 'next' && this.currentPage < this.activeTotalPages) {
      this.currentPage++;
    }
  }

  goToCompletedPage(direction: 'prev' | 'next') {
    if (direction === 'prev' && this.completedPage > 1) {
      this.completedPage--;
    } else if (direction === 'next' && this.completedPage < this.completedTotalPages) {
      this.completedPage++;
    }
  }

  toggleCompletedExpanded() {
    this.completedExpanded = !this.completedExpanded;
    if (this.completedExpanded) {
      this.completedPage = 1;
    }
  }

  formatDate(date?: string | null) {
    const key = this.dateKey(date);
    if (!key) {
      return this.translate.instant('CONTROLTASK.NO_DATE');
    }
    const [, month, day] = key.split('-');
    return `${day}/${month}`;
  }

  latestCommentDate(task: ControlTask): string {
    if (!task?.comments?.length) {
      return this.translate.instant('CONTROLTASK.NO_UPDATE');
    }
    const timestamps = task.comments
      .map((comment: any) => comment.created_at || comment.createdAt)
      .filter(Boolean)
      .map((value: any) => new Date(value).getTime())
      .filter((value: number) => !Number.isNaN(value));

    if (!timestamps.length) {
      return this.translate.instant('CONTROLTASK.NO_UPDATE');
    }

    const latest = Math.max(...timestamps);
    const locale = this.translate.currentLang === 'en' ? 'en-US' : 'pt-BR';
    return new Date(latest).toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  statusClass(status: string) {
    const normalized = (status || '').toLowerCase();
    return {
      open: normalized === 'open',
      inprogress: normalized === 'in_progress',
      paused: normalized === 'paused',
      blocked: normalized === 'blocked',
      completed: normalized === 'completed',
      awaitingClient: normalized === 'awaiting_client_response',
      awaitingBudget: normalized === 'awaiting_budget_approval',
      awaitingSupplier: normalized === 'awaiting_supplier_response'
    };
  }

  translateStatus(status: string | null | undefined) {
    const map: Record<string, string> = {
      OPEN: 'CONTROLTASK.STATUS.OPEN',
      IN_PROGRESS: 'CONTROLTASK.STATUS.IN_PROGRESS',
      PAUSED: 'CONTROLTASK.STATUS.PAUSED',
      BLOCKED: 'CONTROLTASK.STATUS.BLOCKED',
      COMPLETED: 'CONTROLTASK.STATUS.COMPLETED',
      AWAITING_CLIENT_RESPONSE: 'CONTROLTASK.STATUS.AWAITING_CLIENT_RESPONSE',
      AWAITING_BUDGET_APPROVAL: 'CONTROLTASK.STATUS.AWAITING_BUDGET_APPROVAL',
      AWAITING_SUPPLIER_RESPONSE: 'CONTROLTASK.STATUS.AWAITING_SUPPLIER_RESPONSE'
    };
    if (!status) {
      return this.translate.instant('CONTROLTASK.NO_STATUS');
    }
    const upper = status.toUpperCase();
    return map[upper] ? this.translate.instant(map[upper]) : status;
  }

  formatSecondsAsDuration(value?: number | null) {
    const totalSeconds = Math.max(0, Math.floor(Number(value || 0)));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts: string[] = [];
    if (hours > 0) {
      parts.push(`${hours}h`);
    }
    if (minutes > 0 || !parts.length) {
      parts.push(`${minutes}m`);
    }
    if (hours > 0 || minutes > 0) {
      parts.push(`${String(seconds).padStart(2, '0')}s`);
    }
    return parts.join(' ');
  }

  taskExecutionSeconds(task: ControlTask) {
    return Number(task.execution_elapsed_seconds ?? task.execution_total_seconds ?? 0) || 0;
  }

  taskExecutionLabel(task: ControlTask) {
    return this.formatSecondsAsDuration(this.taskExecutionSeconds(task));
  }

  taskExecutionStateLabel(task: ControlTask) {
    if (task.status === 'IN_PROGRESS') {
      return this.translate.instant('CONTROLTASK.EXECUTION.RUNNING');
    }
    if (task.status === 'COMPLETED') {
      return this.translate.instant('CONTROLTASK.EXECUTION.COMPLETED');
    }
    return this.translate.instant('CONTROLTASK.EXECUTION.PAUSED');
  }

  isTaskExecuting(task: ControlTask) {
    return task.status === 'IN_PROGRESS';
  }

  openTaskDetail(task: ControlTask) {
    this.activeTask = task;
    const taskMembers = task.members || [];
    const assignee =
      taskMembers.find((member: any) => member.role === 'ASSIGNEE') ||
      taskMembers.find((member: any) => member.role === 'OWNER');
    this.taskDetailForm.reset({
      title: task.title,
      description: task.description || '',
      status: task.status,
      priority: task.priority,
      visibility: task.visibility,
      start_date: task.start_date ? this.toInputDate(task.start_date) : '',
      due_date: task.due_date ? this.toInputDate(task.due_date) : '',
      groupId: task.groups?.[0]?.id || '',
      subgroupId: task.subgroup_id || '',
      bucketId: task.bucket_id || '',
      tagIds: (task.tags || []).map((tag: any) => tag.id),
      assigneeId: assignee?.id || ''
    });
    this.handleDetailGroupChange();
    this.handleDetailSubgroupChange();
    this.detailSubtaskText = '';
    this.detailCommentText = '';
    this.showTaskDetailModal = true;
  }

  closeTaskDetail() {
    this.showTaskDetailModal = false;
    this.activeTask = null;
  }

  deleteTask(task: ControlTask) {
    if (!this.tenantId) {
      return;
    }
    const label = task?.title?.trim() ? `"${task.title.trim()}"` : this.translate.instant('CONTROLTASK.THIS_TASK');
    const message = this.translate.instant('CONTROLTASK.CONFIRM_DELETE', { label });
    if (!confirm(message)) {
      return;
    }
    this.service.deleteTask(task.id, this.tenantId).subscribe({
      next: () => {
        this.tasks = this.tasks.filter((item) => item.id !== task.id);
        if (this.activeTask?.id === task.id) {
          this.closeTaskDetail();
        }
        this.loadSummary();
      }
    });
  }

  get detailFormSubgroups() {
    const selectedGroup = Number(this.taskDetailForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      return this.subgroups;
    }
    return this.subgroups.filter((subgroup) => subgroup.group_id === selectedGroup);
  }

  get detailFormBuckets() {
    const subgroupId = Number(this.taskDetailForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      return [];
    }
    return this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
  }

  handleDetailGroupChange() {
    const selectedGroup = Number(this.taskDetailForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      this.taskDetailForm.patchValue({ subgroupId: '', bucketId: '' });
      return;
    }
    const subgroupId = Number(this.taskDetailForm.get('subgroupId')?.value) || null;
    if (subgroupId) {
      const subgroup = this.subgroups.find((item) => item.id === subgroupId);
      if (!subgroup || subgroup.group_id !== selectedGroup) {
        this.taskDetailForm.patchValue({ subgroupId: '', bucketId: '' });
      }
    }
  }

  handleDetailSubgroupChange() {
    const subgroupId = Number(this.taskDetailForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      this.taskDetailForm.patchValue({ bucketId: '' });
      return;
    }
    const available = this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
    if (!available.length) {
      this.taskDetailForm.patchValue({ bucketId: '' });
      return;
    }
    const currentBucket = Number(this.taskDetailForm.get('bucketId')?.value) || null;
    if (!currentBucket || !available.some((bucket) => bucket.id === currentBucket)) {
      this.taskDetailForm.patchValue({ bucketId: available[0].id });
    }
  }

  submitTaskDetail() {
    if (!this.activeTask) {
      return;
    }
    const value = this.taskDetailForm.value;
    const payload = {
      tenant_id: this.tenantId,
      user_id: this.userId,
      title: value.title,
      description: value.description,
      status: value.status,
      priority: value.priority,
      visibility: value.visibility,
      start_date: value.start_date || null,
      due_date: value.due_date || null,
      subgroup_id: value.subgroupId || null,
      bucket_id: value.bucketId || null,
      groups: value.groupId ? [Number(value.groupId)] : [],
      tags: value.tagIds || [],
      assignees: value.assigneeId ? [Number(value.assigneeId)] : []
    };
    this.service.updateTask(this.activeTask.id, payload).subscribe({
      next: () => {
        this.closeTaskDetail();
        this.loadTasks();
      }
    });
  }

  addDetailSubtask() {
    if (!this.activeTask || !this.detailSubtaskText.trim()) {
      return;
    }
    const orderIndex = (this.activeTask.subtasks || []).length;
    this.service.createSubtask(this.activeTask.id, { title: this.detailSubtaskText.trim(), order_index: orderIndex }).subscribe({
      next: (response: any) => {
        this.activeTask!.subtasks = this.activeTask!.subtasks || [];
        this.activeTask!.subtasks.push({
          id: response.id,
          title: this.detailSubtaskText.trim(),
          completed: 0,
          order_index: orderIndex
        });
        this.detailSubtaskText = '';
      }
    });
  }

  toggleDetailSubtask(subtask: any) {
    if (!this.activeTask) {
      return;
    }
    this.toggleSubtask(this.activeTask, subtask);
  }

  updateSubtaskDueDate(subtask: any, event: Event) {
    const input = event.target as HTMLInputElement | null;
    if (!input || !this.activeTask) {
      return;
    }
    const value = input.value || null;
    this.service.updateSubtask(subtask.id, { due_date: value }).subscribe({
      next: () => {
        subtask.due_date = value;
      }
    });
  }

  reorderDetailSubtasks(event: CdkDragDrop<any[]>) {
    if (!this.activeTask?.subtasks?.length) {
      return;
    }
    moveItemInArray(this.activeTask.subtasks, event.previousIndex, event.currentIndex);
    this.activeTask.subtasks.forEach((subtask, index) => {
      subtask.order_index = index;
    });
    const updates = this.activeTask.subtasks.map((subtask) =>
      firstValueFrom(this.service.updateSubtask(subtask.id, { order_index: subtask.order_index }))
    );
    Promise.all(updates).catch(() => {});
  }

  startEditSubtask(subtask: any) {
    this.editingSubtaskId = subtask.id;
    this.editingSubtaskText = subtask.title || '';
  }

  cancelEditSubtask() {
    this.editingSubtaskId = null;
    this.editingSubtaskText = '';
  }

  saveEditSubtask(subtask: any) {
    if (!this.editingSubtaskText.trim()) {
      return;
    }
    this.service.updateSubtask(subtask.id, { title: this.editingSubtaskText.trim() }).subscribe({
      next: () => {
        subtask.title = this.editingSubtaskText.trim();
        this.cancelEditSubtask();
      }
    });
  }

  deleteSubtask(subtask: any) {
    this.service.deleteSubtask(subtask.id).subscribe({
      next: () => {
        if (!this.activeTask) {
          return;
        }
        this.activeTask.subtasks = (this.activeTask.subtasks || []).filter((item: any) => item.id !== subtask.id);
      }
    });
  }

  openSubtaskDatePicker(input: HTMLInputElement | null) {
    if (!input) {
      return;
    }
    if (typeof (input as any).showPicker === 'function') {
      (input as any).showPicker();
    } else {
      input.click();
    }
  }

  addDetailComment() {
    if (!this.activeTask || !this.userId) {
      return;
    }
    const text = this.detailCommentText.trim();
    const hasFile = !!this.detailCommentFile;
    if (!text && !hasFile) {
      return;
    }

    let payload: any;
    if (hasFile) {
      const formData = new FormData();
      formData.append('user_id', String(this.userId));
      if (this.tenantId) {
        formData.append('tenant_id', String(this.tenantId));
      }
      formData.append('comment', text);
      formData.append('attachment', this.detailCommentFile as File);
      payload = formData;
    } else {
      payload = {
        user_id: this.userId,
        tenant_id: this.tenantId,
        comment: text
      };
    }

    this.service.addComment(this.activeTask.id, payload).subscribe({
      next: (response: any) => {
        const formatted = {
          id: response.id,
          text: response.comment || response.text,
          author: response.author_name || response.author || this.translate.instant('CONTROLTASK.DETAIL.YOU'),
          created_at: response.created_at,
          attachments: response.attachments || []
        };
        this.activeTask!.comments = this.activeTask!.comments || [];
        this.activeTask!.comments.unshift(formatted);
        this.detailCommentText = '';
        this.detailCommentFile = null;
        if (this.commentFileInputElement) {
          this.commentFileInputElement.value = '';
        }
      }
    });
  }

  startEditComment(comment: any) {
    this.editingCommentId = comment.id;
    this.editingCommentText = comment.comment || comment.text || '';
  }

  cancelEditComment() {
    this.editingCommentId = null;
    this.editingCommentText = '';
  }

  saveEditComment(comment: any) {
    if (!this.tenantId || !this.editingCommentText.trim()) {
      return;
    }
    this.service.updateComment(comment.id, { tenant_id: this.tenantId, comment: this.editingCommentText.trim() }).subscribe({
      next: () => {
        comment.comment = this.editingCommentText.trim();
        comment.text = this.editingCommentText.trim();
        this.cancelEditComment();
      }
    });
  }

  deleteComment(comment: any) {
    if (!this.tenantId) {
      return;
    }
    this.service.deleteComment(this.tenantId, comment.id).subscribe({
      next: () => {
        if (!this.activeTask) {
          return;
        }
        this.activeTask.comments = (this.activeTask.comments || []).filter((item: any) => item.id !== comment.id);
      }
    });
  }

  removeCommentAttachment(comment: any, attachment: any) {
    if (!this.tenantId || !attachment?.id) {
      return;
    }
    this.service.removeCommentAttachment(this.tenantId, attachment.id).subscribe({
      next: () => {
        comment.attachments = (comment.attachments || []).filter((item: any) => item.id !== attachment.id);
      }
    });
  }

  onCommentFileSelected(event: Event) {
    const input = event.target as HTMLInputElement | null;
    this.commentFileInputElement = input;
    const file = input?.files?.[0] || null;
    this.detailCommentFile = file;
  }

  removeCommentFile() {
    this.detailCommentFile = null;
    if (this.commentFileInputElement) {
      this.commentFileInputElement.value = '';
    }
  }

  formatCommentFile(path?: string | null) {
    if (!path) {
      return '';
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.fileHostUrl}${path}`;
  }

  taskAttachmentUrl(file?: { file_path?: string | null }) {
    const path = file?.file_path;
    if (!path) {
      return '#';
    }
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.fileHostUrl}${path}`;
  }

  applyDescriptionFormat(textarea: HTMLTextAreaElement, format: 'bold' | 'italic' | 'emoji' | 'bullet') {
    const value = textarea.value;
    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    let wrapperStart = '';
    let wrapperEnd = '';
    let extra = '';

    switch (format) {
      case 'bold':
        wrapperStart = '**';
        wrapperEnd = '**';
        break;
      case 'italic':
        wrapperStart = '_';
        wrapperEnd = '_';
        break;
      case 'bullet':
        wrapperStart = '\n- ';
        break;
      case 'emoji':
        extra = ' 😊';
        break;
    }

    let newValue: string;
    if (format === 'emoji') {
      newValue = `${value.slice(0, end)}${extra}${value.slice(end)}`;
      this.taskDetailForm.patchValue({ description: newValue });
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = end + extra.length;
      });
      return;
    }

    newValue = `${value.slice(0, start)}${wrapperStart}${value.slice(start, end)}${wrapperEnd}${value.slice(end)}`;
    this.taskDetailForm.patchValue({ description: newValue });
    setTimeout(() => {
      textarea.focus();
      const cursor = end + wrapperStart.length + wrapperEnd.length;
      textarea.selectionStart = textarea.selectionEnd = cursor;
    });
  }

  toInputDate(value: string) {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private generateTaskCode() {
    const seed = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += seed[Math.floor(Math.random() * seed.length)];
    }
    return result;
  }

  completedSubtasks(task: ControlTask) {
    if (!task.subtasks?.length) {
      return 0;
    }
    return task.subtasks.filter((item) => !!item.completed).length;
  }
}
